import { DefaultRequestStatus, RequestStatus } from "ecom-teams-apps-core";
import { action, makeObservable, observable, runInAction } from "mobx";
import { TaskDto } from "../service/api/TaskDto";
import defaultTasksService, { TasksService } from "../service/TasksService";
import { Task } from "../../shared/models/Task";
import { TaskFromResponse } from "../service/TaskFromResponse";
import { TBoardItems } from "msTeamsUI/components/Board/BoardItem";
import { DateRange } from "shared/models/DateRange";
import { DictionaryItemDto } from "tasksBoard/service/api/DictionaryItemDto";
import { UserIdentity } from "shared/models/UserIdentity";
import { UserIdentityDto } from "tasksBoard/service/api/UserIdentityDto";
import { UserIdentityFromResponse } from "tasksBoard/service/UserIdentityFromResponse";
import fileDownload from "js-file-download";

export interface TasksStore {
    tasks: Task[];
    selectedTask: Task | null;
    boardItems: TBoardItems;
    boardItemsLoaded: boolean;
    availableAssignees: UserIdentity[];
    lastUpdatedCommentId: number | null;
    selectedTaskTags: string[];
    selectedAgencies: string[];
    selectedPeople: string[];
    filteredBoardItems: TBoardItems;

    loadTasksRequestStatus: RequestStatus;
    loadTaskRequestStatus: RequestStatus;
    updateTaskStatusRequestStatus: RequestStatus;
    updateTaskAttributeRequestStatus: RequestStatus;
    addCommentRequestStatus: RequestStatus;
    updateCommentRequestStatus: RequestStatus;
    deleteCommentRequestStatus: RequestStatus;
    updatePlannedDateRequestStatus: RequestStatus;
    assignUserRequestStatus: RequestStatus;
    loadAvailableAssigneesRequestStatus: RequestStatus;
    addAttachmentRequestStatus: RequestStatus;
    removeAttachmentRequestStatus: RequestStatus;
    uploadFileRequestStatus: RequestStatus;
    getFileTokenRequestStatus: RequestStatus;
    retreiveFileRequestStatus: RequestStatus;
    requestStatusesWithError: RequestStatus[];
    isSelectedTaskStatusPending: boolean;

    validationMessage: string | null;
    setValidationMessage: (message: string | null) => void;

    resetAllRequestStatuses: () => void;
    loadTasks: (range: DateRange) => void;
    loadTask: (taskId: number) => void;
    updateTaskStatus: (taskId: number, previousTaskId: number, status: string, newStatusId: number) => Promise<boolean>;
    updateTaskAttribute: (taskId: number, attributeId: number, customValue: DictionaryItemDto) => void;
    updateTaskAttributeMulti: (taskId: number, attributeId: number, customValues: DictionaryItemDto[]) => void;
    addComment: (taskId: number, content: string) => void;
    updateComment: (taskId: number, commentId: number, content: string) => void;
    deleteComment: (taskId: number, commentId: number) => void;
    updatePlannedDate: (taskId: number, date: Date) => void;
    assignUser: (taskId: number, userName: string) => void;
    loadAvailableAssignees: (taskId: number) => void;
    addAttachment: (taskId: number, attachmentHash: string) => void;
    removeAttachment: (taskId: number, attachmentId: number) => void;
    uploadFile: (file: File) => Promise<string | undefined>;
    getFileToken: (attachmentHash: string) => Promise<string | undefined>;
    retrieveFile: (filename: string, token: string) => void;
    setSelectedTaskTags: (selectedTags: string[]) => void;
    setSelectedAgencies: (selectedAgencies: string[]) => void;
    setSelectedPeople: (selectedPeople: string[]) => void;
    clearFilters: () => void;
}

export class DefaultTasksStore implements TasksStore {
    _lastUpdatedTaskId: number | null = null;

    constructor(
        private tasksService: TasksService,
        private loadTasksReqStatus: RequestStatus,
        private loadTaskReqStatus: RequestStatus,
        private updateTaskStatusReqStatus: RequestStatus,
        private updateTaskAttributeReqStatus: RequestStatus,
        private addCommentReqStatus: RequestStatus,
        private updateCommentReqStatus: RequestStatus,
        private deleteCommentReqStatus: RequestStatus,
        private updatePlannedDateReqStatus: RequestStatus,
        private assignUserReqStatus: RequestStatus,
        private loadAvailableAssigneesReqStatus: RequestStatus,
        private addAttachmentReqStatus: RequestStatus,
        private removeAttachmentReqStatus: RequestStatus,
        private uploadFileReqStatus: RequestStatus,
        private getFileTokenReqStatus: RequestStatus,
        private retreiveFileReqStatus: RequestStatus
    ) {
        makeObservable(this, {
            loadTasks: action,
            loadTask: action,
            updateTaskStatus: action,
            updateTaskAttribute: action,
            updateTaskAttributeMulti: action,
            addComment: action,
            updateComment: action,
            deleteComment: action,
            updatePlannedDate: action,
            assignUser: action,
            loadAvailableAssignees: action,
            addAttachment: action,
            removeAttachment: action,
            uploadFile: action,
            getFileToken: action,
            retrieveFile: action,
            tasks: observable,
            selectedTask: observable,
            boardItems: observable,
            boardItemsLoaded: observable,
            availableAssignees: observable,
            selectedTaskTags: observable,
            setSelectedTaskTags: action,
            selectedAgencies: observable,
            setSelectedAgencies: action,
            validationMessage: observable,
            setValidationMessage: action,
            selectedPeople: observable,
            setSelectedPeople: action,
            clearFilters: action,
        });
    }

    public tasks: Task[] = [];
    public selectedTask: Task | null = null;
    public boardItems: TBoardItems = {};
    public boardItemsLoaded: boolean = false;
    public availableAssignees: UserIdentity[] = [];
    public lastUpdatedCommentId: number | null = null;
    public selectedTaskTags: string[] = [];
    public selectedAgencies: string[] = [];
    public validationMessage: string | null = null;
    public selectedPeople: string[] = [];

    public get loadTasksRequestStatus() {
        return this.loadTasksReqStatus;
    }

    public get loadTaskRequestStatus() {
        return this.loadTaskReqStatus;
    }

    public get updateTaskStatusRequestStatus() {
        return this.updateTaskStatusReqStatus;
    }

    public get updateTaskAttributeRequestStatus() {
        return this.updateTaskAttributeReqStatus;
    }

    public get addCommentRequestStatus() {
        return this.addCommentReqStatus;
    }

    public get updateCommentRequestStatus() {
        return this.updateCommentReqStatus;
    }

    public get deleteCommentRequestStatus() {
        return this.deleteCommentReqStatus;
    }

    public get updatePlannedDateRequestStatus() {
        return this.updatePlannedDateReqStatus;
    }

    public get assignUserRequestStatus() {
        return this.assignUserReqStatus;
    }

    public get loadAvailableAssigneesRequestStatus() {
        return this.loadAvailableAssigneesReqStatus;
    }

    public get addAttachmentRequestStatus() {
        return this.addAttachmentReqStatus;
    }

    public get removeAttachmentRequestStatus() {
        return this.removeAttachmentReqStatus;
    }

    public get uploadFileRequestStatus() {
        return this.uploadFileReqStatus;
    }

    public get getFileTokenRequestStatus() {
        return this.getFileTokenReqStatus;
    }

    public get retreiveFileRequestStatus() {
        return this.retreiveFileReqStatus;
    }

    public get requestStatusesWithError() {
        const currentStoreStatuses = Object.entries(this).filter((x) => x[0].includes("ReqStatus"));
        const statusesWithErrors: RequestStatus[] = [];

        for (const requestStatus of currentStoreStatuses) {
            const req = requestStatus[1] as RequestStatus;

            if (req.isDone && req.error) {
                statusesWithErrors.push(req);
            }
        }

        return statusesWithErrors;
    }

    public get isSelectedTaskStatusPending() {
        return this.updateTaskStatusReqStatus.isPending && this._lastUpdatedTaskId === this.selectedTask?.id;
    }

    public get filteredBoardItems() {
        const filteredTasks = this.tasks
            .filter(
                (task) =>
                    this.selectedTaskTags.length === 0 ||
                    this.selectedTaskTags.some((tag) => task.tags.map((tag) => tag.description).includes(tag))
            )
            .filter(
                (task) =>
                    (this.selectedAgencies.length === 0 && this.selectedPeople.length === 0) ||
                    this.selectedAgencies.includes(task.intermediaryDisplayName!) ||
                    this.selectedPeople.includes(task.sellerInformation!)
            );

        return this.mapTasksToBoardItems(filteredTasks);
    }

    public setSelectedTaskTags(taskTags: string[]) {
        this.selectedTaskTags = taskTags;
    }

    public setSelectedAgencies(agencies: string[]) {
        this.selectedAgencies = agencies;
    }

    public setSelectedPeople(people: string[]) {
        this.selectedPeople = people;
    }

    public resetAllRequestStatuses() {
        for (const requestStatus of this.requestStatusesWithError) {
            requestStatus.reset();
        }
    }

    public clearFilters() {
        this.setSelectedTaskTags([]);
        this.setSelectedAgencies([]);
        this.setSelectedPeople([]);
    }

    public async loadTasks(range: DateRange) {
        try {
            this.boardItemsLoaded = false;
            let fetchedTasks = await this.loadTasksReqStatus.handleAnyRequest(() => this.tasksService.getTasks(range));

            runInAction(() => {
                if (fetchedTasks) {
                    this.tasks = fetchedTasks.map((dto: TaskDto) => new TaskFromResponse(dto).task());
                    this.boardItems = this.mapTasksToBoardItems(this.tasks);
                }
            });
        } finally {
            runInAction(() => {
                this.boardItemsLoaded = true;
            });
        }
    }

    public async loadTask(taskId: number) {
        this.selectedTask = null;
        let task = await this.loadTaskReqStatus.handleAnyRequest(() => this.tasksService.getTask(taskId));

        runInAction(() => {
            if (task) {
                this.selectedTask = new TaskFromResponse(task).task();
            }
        });
    }

    public async updateTaskStatus(
        taskId: number,
        previousTaskId: number,
        status: string,
        newStatusId: number
    ): Promise<boolean> {
        this._lastUpdatedTaskId = taskId;

        await this.updateTaskStatusReqStatus.handleAnyRequest(
            () =>
                this.tasksService.updateTaskStatus({
                    issueId: taskId,
                    previousId: previousTaskId > 0 ? previousTaskId : null,
                    status: { code: status },
                }),
            true
        );

        const successfullyUpdatedTaskStatus = await new Promise<boolean>((resolve, reject) => {
            runInAction(() => {
                if (this.updateTaskStatusReqStatus.error) {
                    this.setValidationMessage(this.updateTaskStatusReqStatus.error);
                    resolve(false);
                }

                if (this.selectedTask && this.selectedTask?.id === taskId) {
                    this.selectedTask.status.id = newStatusId;
                    this.selectedTask.statusDate = new Date();
                }

                resolve(true);
            });
        });

        // Change the task status and its order in column using in memory tasks, don't fetch all the tasks on each task status change to prevent glitchiness
        if (successfullyUpdatedTaskStatus) {
            const updatedTask = this.tasks.find((task) => task.id === taskId);
            if (updatedTask) {
                updatedTask.status.id = newStatusId;
                updatedTask.status.code = status;

                const currentTaskIndex = this.tasks.indexOf(updatedTask);

                const previousTask = this.tasks.find((task) => task.id === previousTaskId);
                if (previousTask) {
                    const previousTaskIndex = this.tasks.indexOf(previousTask);
                    this.tasks.splice(currentTaskIndex, 1);
                    this.tasks.splice(previousTaskIndex + 1, 0, updatedTask);
                } else {
                    this.tasks.splice(currentTaskIndex, 1);
                    this.tasks.splice(0, 0, updatedTask);
                }
            }
        }

        return successfullyUpdatedTaskStatus;
    }

    public async updateTaskAttribute(taskId: number, attributeId: number, customValue: DictionaryItemDto) {
        await this.updateTaskAttributeReqStatus.handleAnyRequest(() =>
            this.tasksService.updateTaskAttribute(taskId, attributeId, customValue)
        );
    }

    public async updateTaskAttributeMulti(taskId: number, attributeId: number, customValues: DictionaryItemDto[]) {
        let task = await this.updateTaskAttributeReqStatus.handleAnyRequest(() =>
            this.tasksService.updateTaskAttributeMulti(taskId, attributeId, customValues)
        );

        if (this.updateTaskAttributeReqStatus.error) {
            return;
        }

        runInAction(() => {
            if (task && this.selectedTask) {
                this.selectedTask.operationList = new TaskFromResponse(task).task().operationList;
            }
        });
    }

    public async addComment(taskId: number, content: string) {
        await this.addCommentReqStatus.handleAnyRequest(() => this.tasksService.addComment(taskId, content));
    }

    public async updateComment(taskId: number, commentId: number, content: string) {
        await this.updateCommentReqStatus.handleAnyRequest(() =>
            this.tasksService.updateComment(taskId, commentId, content)
        );

        this.lastUpdatedCommentId = commentId;

        if (!this.updateCommentReqStatus.error) {
            const comment = this.selectedTask?.comments.find((x) => x.id === commentId);

            if (!comment) {
                return;
            }

            comment.content = content;
        }
    }

    public async deleteComment(taskId: number, commentId: number) {
        await this.deleteCommentReqStatus.handleAnyRequest(() => this.tasksService.deleteComment(taskId, commentId));

        if (this.deleteCommentReqStatus.error) {
            return;
        }

        runInAction(() => {
            if (this.selectedTask?.comments) {
                this.selectedTask.comments = this.selectedTask?.comments.filter((x) => x.id !== commentId);
            }
        });
    }

    public async updatePlannedDate(taskId: number, date: Date) {
        await this.updatePlannedDateReqStatus.handleAnyRequest(() => this.tasksService.updatePlannedDate(taskId, date));
    }

    public async assignUser(taskId: number, userName: string) {
        await this.assignUserReqStatus.handleAnyRequest(() => this.tasksService.assignUser(taskId, userName));
    }

    public async loadAvailableAssignees(taskId: number) {
        let fetchedUsers = await this.loadAvailableAssigneesReqStatus.handleAnyRequest(() =>
            this.tasksService.getAvailableAssignees(taskId)
        );

        runInAction(() => {
            if (fetchedUsers) {
                this.availableAssignees = fetchedUsers.map((dto: UserIdentityDto) =>
                    new UserIdentityFromResponse(dto).userIdentity()
                );
            }
        });
    }

    public async addAttachment(taskId: number, attachmentHash: string) {
        const task = await this.addAttachmentReqStatus.handleAnyRequest(() =>
            this.tasksService.addAttachment(taskId, attachmentHash)
        );

        runInAction(() => {
            if (task) {
                this.selectedTask = new TaskFromResponse(task).task();
            }
        });
    }

    public async removeAttachment(taskId: number, attachmentId: number) {
        const task = await this.removeAttachmentReqStatus.handleAnyRequest(() =>
            this.tasksService.removeAttachment(taskId, attachmentId)
        );

        runInAction(() => {
            if (task) {
                this.selectedTask = new TaskFromResponse(task).task();
            }
        });
    }

    public async uploadFile(file: File) {
        const result = await this.uploadFileReqStatus.handleAnyRequest(() => this.tasksService.uploadFile(file));

        return result?.hash;
    }

    public async getFileToken(attachmentHash: string) {
        const result = await this.getFileTokenReqStatus.handleAnyRequest(() =>
            this.tasksService.getFileToken(attachmentHash)
        );

        return result?.token;
    }

    public async retrieveFile(filename: string, token: string) {
        const result = await this.retreiveFileReqStatus.handleAnyRequest(() => this.tasksService.retrieveFile(token));

        if (!result) {
            return;
        }

        fileDownload(result, filename);
    }

    public setValidationMessage(message: string | null) {
        this.validationMessage = message;
    }

    private mapTasksToBoardItems = (taskItems: Task[]) => {
        const tasks: TBoardItems = {};

        const taskStatusCounter: { [taskStatusKey: string]: number } = {};

        taskItems.forEach((t) => {
            if (!taskStatusCounter[t.status.code]) {
                taskStatusCounter[t.status.code] = 0;
            }

            tasks[t.id] = {
                id: t.id,
                lane: t.status.code,
                order: taskStatusCounter[t.status.code],
                title: t.subject,
                subtitle: t.description,
                agency: t.intermediaryDisplayName,
                agent: t.sellerInformation,
                tags: t.tags,
                users: t.assignee ? [t.assignee.fullName || t.assignee.userName] : [],
            };

            taskStatusCounter[t.status.code] = taskStatusCounter[t.status.code] + 1;
        });

        return tasks;
    };
}

export default new DefaultTasksStore(
    defaultTasksService,
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus()
);
